import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { formatDateToUTC, formatDbTimestampToLocal } from "@/lib/utils/date";
import { ActivityType } from "@/types/activity";
import { ActivityEditFormState, ActivityUpdatePayload } from "@/types/activities/activityEdit";
import { updateActivity } from "@/lib/services/activity-service";

/**
 * Custom hook to manage activity edit form state and logic
 */
export function useActivityEditForm(
  activity: ActivityType | null,
  activityType: 'sleep' | 'nap' | 'feeding',
  onClose: () => void
) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Initialize form state
  const [formState, setFormState] = useState<ActivityEditFormState>({
    notes: '',
    startDate: undefined,
    startTime: '',
    endDate: undefined,
    endTime: '',
    timeValid: true,
    locationId: '',
    environmentId: '',
    onsetMethodId: '',
    restfulnessId: '',
    signsOfSleepDebt: false,
    soundId: '',
    soundLevelId: '',
    isSubmitting: false
  });
  
  // Create field setter functions
  const setField = <K extends keyof ActivityEditFormState>(
    field: K, 
    value: ActivityEditFormState[K]
  ) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };
  
  // Initialize form when activity changes
  useEffect(() => {
    if (activity) {
      // Parse dates from ISO strings
      const startDateTime = activity.startTime ? new Date(activity.startTime) : new Date();
      const endDateTime = activity.endTime ? new Date(activity.endTime) : new Date();
      
      // Set base values for all activity types
      let updatedState: Partial<ActivityEditFormState> = {
        notes: activity.notes || '',
        startDate: startDateTime,
        startTime: format(startDateTime, 'HH:mm'),
        endDate: endDateTime,
        endTime: format(endDateTime, 'HH:mm'),
        timeValid: true
      };
      
      // Add sleep-specific fields
      if (activityType === 'sleep') {
        updatedState = {
          ...updatedState,
          locationId: activity.location || '',
          environmentId: activity.environment || '',
          onsetMethodId: activity.onset_method || '',
          restfulnessId: activity.quality || '',
          signsOfSleepDebt: activity.signs_of_sleep_debt || false,
          soundId: activity.sound || '',
          soundLevelId: activity.sound_level || ''
        };
      } 
      // Add nap-specific fields
      else if (activityType === 'nap') {
        updatedState = {
          ...updatedState,
          locationId: activity.location || '',
          environmentId: activity.environment || '',
          onsetMethodId: activity.onset_method || '',
          restfulnessId: activity.quality || '',
          signsOfSleepDebt: activity.signs_of_sleep_debt || false
        };
      }
      // For feeding or any other activity types, 
      // we just set the common properties and reset the others
      else {
        updatedState = {
          ...updatedState,
          locationId: '',
          environmentId: '',
          onsetMethodId: '',
          restfulnessId: '',
          signsOfSleepDebt: false,
          soundId: '',
          soundLevelId: ''
        };
      }
      
      // Update the state
      setFormState(prev => ({
        ...prev,
        ...updatedState,
        isSubmitting: false
      }));
      
      // Log timezone info for debugging
      console.log(`[ActivityEditForm] Initializing edit form with timezone info:`);
      console.log(`  User timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
      console.log(`  Original start: ${activity.startTime}`);
      console.log(`  Formatted start: ${formatDbTimestampToLocal(activity.startTime, 'MM/DD/YYYY HH:mm')}`);
    }
  }, [activity, activityType]);
  
  // Validate that end time is after start time
  useEffect(() => {
    if (formState.startDate && formState.endDate && formState.startTime && formState.endTime) {
      // Create full date objects with time
      const start = new Date(formState.startDate);
      const [startHours, startMinutes] = formState.startTime.split(':').map(Number);
      start.setHours(startHours, startMinutes);
      
      const end = new Date(formState.endDate);
      const [endHours, endMinutes] = formState.endTime.split(':').map(Number);
      end.setHours(endHours, endMinutes);
      
      // Set validity state
      setField('timeValid', end > start);
    }
  }, [formState.startDate, formState.endDate, formState.startTime, formState.endTime]);
  
  // Calculate duration from current form values
  const calculateDuration = (): string => {
    if (!formState.startDate || !formState.endDate || !formState.startTime || !formState.endTime || !formState.timeValid) {
      return '';
    }
    
    // Create full date/time objects
    const start = new Date(formState.startDate);
    const [startHours, startMinutes] = formState.startTime.split(':').map(Number);
    start.setHours(startHours, startMinutes);
    
    const end = new Date(formState.endDate);
    const [endHours, endMinutes] = formState.endTime.split(':').map(Number);
    end.setHours(endHours, endMinutes);
    
    // Calculate duration in milliseconds
    const durationMs = end.getTime() - start.getTime();
    
    // Convert to hours and minutes
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    // Format the duration
    if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };
  
  // Handle form submission
  const handleSave = async () => {
    if (!activity) return;
    
    if (!formState.timeValid) {
      toast({
        title: "Invalid time",
        description: "End time must be after start time",
        variant: "destructive"
      });
      return;
    }
    
    setField('isSubmitting', true);
    
    try {
      // Create start and end date objects with time
      const startDateTime = new Date(formState.startDate!);
      const [startHours, startMinutes] = formState.startTime.split(':').map(Number);
      startDateTime.setHours(startHours, startMinutes);
      
      const endDateTime = new Date(formState.endDate!);
      const [endHours, endMinutes] = formState.endTime.split(':').map(Number);
      endDateTime.setHours(endHours, endMinutes);
      
      // Convert to UTC for storage
      const startTimeUTC = formatDateToUTC(startDateTime);
      const endTimeUTC = formatDateToUTC(endDateTime);
      
      // Create update payload with common fields
      const updateData: ActivityUpdatePayload = {
        notes: formState.notes,
        startTime: startTimeUTC,
        endTime: endTimeUTC,
      };
      
      // Add sleep-specific fields
      // Following the database schema: sleep_sessions to onset_methods, restfulness, etc.
      if (activityType === 'sleep') {
        updateData.location = formState.locationId === 'none' ? null : formState.locationId || null;
        updateData.environment = formState.environmentId === 'none' ? null : formState.environmentId || null;
        updateData.onset_method = formState.onsetMethodId === 'none' ? null : formState.onsetMethodId || null;
        updateData.quality = formState.restfulnessId === 'none' ? null : formState.restfulnessId || null;
        updateData.signs_of_sleep_debt = formState.signsOfSleepDebt;
        updateData.sound = formState.soundId === 'none' ? null : formState.soundId || null;
        updateData.sound_level = formState.soundLevelId === 'none' ? null : formState.soundLevelId || null;
      }
      
      // Add nap-specific fields
      // Following the database schema: nap_sessions to sleep_onset_method, restfulness_rating, etc.
      else if (activityType === 'nap') {
        updateData.location = formState.locationId === 'none' ? null : formState.locationId || null;
        updateData.environment = formState.environmentId === 'none' ? null : formState.environmentId || null;
        updateData.onset_method = formState.onsetMethodId === 'none' ? null : formState.onsetMethodId || null;
        updateData.quality = formState.restfulnessId === 'none' ? null : formState.restfulnessId || null;
        updateData.signs_of_sleep_debt = formState.signsOfSleepDebt;
      }
      
      // For feeding type, we only include the common fields
      
      // Log for debugging timezone conversions
      console.log(`[ActivityEditForm] Saving activity with converted times:`);
      console.log(`  Local start: ${startDateTime.toLocaleString()}`);
      console.log(`  UTC start: ${startTimeUTC}`);
      console.log(`  Local end: ${endDateTime.toLocaleString()}`);
      console.log(`  UTC end: ${endTimeUTC}`);
      
      // Update the activity
      await updateActivity(activity.id, activityType, updateData);
      
      // Show success message
      toast({
        title: "Changes saved",
        description: "Your activity has been updated.",
        variant: "default"
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: [`${activityType}_sessions`] });
      queryClient.invalidateQueries({ queryKey: [`activity_data`] });
      
      // Close the dialog
      onClose();
    } catch (error) {
      console.error("Error updating activity:", error);
      toast({
        title: "Error",
        description: "There was a problem updating the activity.",
        variant: "destructive"
      });
    } finally {
      setField('isSubmitting', false);
    }
  };
  
  return {
    formState,
    setField,
    calculateDuration,
    handleSave
  };
}
